import { z } from 'zod';

/**
 * Contracten van de module reporting: trends in plaats van losse berichten (Stap 33).
 *
 * De bereikpagina per bericht (Stap 10) beantwoordt "heeft dít bericht de vloer gehaald". Dit
 * antwoordt op de vraag erna: gaat het beter of slechter, en waar. Dat is een andere vraag met een
 * ander publiek — een directie kijkt naar een lijn over drie maanden, niet naar twaalf
 * bereikpagina's.
 *
 * Twee regels lopen hier overal doorheen:
 *
 * 1. **Aantallen, geen namen.** Op één plek wel: de lijst slapende medewerkers, en die zit achter
 *    een eigen recht én een tenantvlag (ADR-0014).
 * 2. **Geen uitsplitsing onder vijf mensen** (ADR-0041). In een ploeg van drie is "33% bevestigd"
 *    geen percentage maar een naam.
 */

/**
 * Waarlangs een uitsplitsing loopt.
 *
 * `category` uit de bouwprompt bestaat niet: een bericht heeft in dit platform geen categorie. Wat
 * er wél is en wat de vraag van een preventieadviseur dekt, is `kind`: vraagt het bericht een
 * bevestiging of niet. Dat is het verschil tussen een instructie en een aankondiging, en precies
 * daar zit de vraag "halen we onze instructies".
 */
export const reportDimensionSchema = z.enum(['tenant', 'group', 'kind', 'type']);
export type ReportDimension = z.infer<typeof reportDimensionSchema>;

/**
 * Eén punt in een reeks: een dag, en wat er die dag verstuurd en gelezen is.
 *
 * `recipients` is de noemer. Zonder noemer is "veertig gezien" geen cijfer — veertig van veertig is
 * iets anders dan veertig van vierhonderd.
 */
export const reachPointSchema = z.object({
  /** `YYYY-MM-DD`, de dag waarop de berichten gepubliceerd zijn. */
  date: z.string(),
  recipients: z.number().int(),
  seen: z.number().int(),
  acknowledged: z.number().int(),
  /** Hoeveel berichten er die dag gepubliceerd zijn; anders is een piek niet te lezen. */
  posts: z.number().int(),
  /**
   * Reacties, opmerkingen, peilingdeelnames en bevestigingen op de berichten van die dag (FBK-11).
   * Gedeeld door `recipients` is dat de betrokkenheid.
   */
  interactions: z.number().int().default(0),
  /** Klikken op links in die berichten (FBK-11). */
  clicks: z.number().int().default(0),
});
export type ReachPoint = z.infer<typeof reachPointSchema>;

/**
 * Eén regel van een uitsplitsing: een afdeling, een ploeg, of "instructies" tegen "aankondigingen".
 *
 * `suppressed` betekent: te klein om apart te tonen. De aantallen staan dan op `null` en niet op
 * `0` — "niemand" en "we zeggen het niet" zijn twee verschillende dingen (ADR-0049).
 */
export const reachBreakdownLineSchema = z.object({
  dimensionId: z.string(),
  label: z.string(),
  recipients: z.number().int().nullable(),
  seen: z.number().int().nullable(),
  acknowledged: z.number().int().nullable(),
  /** Berichten, interacties en klikken (FBK-11); 0 bij een groep, null als de regel verborgen is. */
  posts: z.number().int().nullable().default(null),
  interactions: z.number().int().nullable().default(null),
  clicks: z.number().int().nullable().default(null),
  suppressed: z.boolean(),
});
export type ReachBreakdownLine = z.infer<typeof reachBreakdownLineSchema>;

export const reachReportSchema = z.object({
  from: z.string(),
  to: z.string(),
  /** De reeks over tijd, altijd op bedrijfsniveau. */
  series: z.array(reachPointSchema),
  /** De uitsplitsing over dezelfde periode, opgeteld. */
  dimension: reportDimensionSchema,
  breakdown: z.array(reachBreakdownLineSchema),
  /**
   * Hoeveel regels er samengevoegd zijn omdat ze te klein waren. Nul betekent: je ziet alles.
   *
   * Dit getal staat er bewust bij. Een rapport dat stil drie afdelingen weglaat, is een rapport
   * waarvan niemand weet dat het incompleet is.
   */
  suppressedLines: z.number().int(),
});
export type ReachReport = z.infer<typeof reachReportSchema>;

/**
 * De activatietrechter: van ingevoerd naar werkelijk in gebruik.
 *
 * Vier getallen die elkaar insluiten. De interessante is de laatste: een account dat geactiveerd is
 * maar negentig dagen niet gebruikt, is geen bereik.
 */
export const activationPointSchema = z.object({
  date: z.string(),
  /** Medewerkers in dienst. De noemer van de hele trechter. */
  people: z.number().int(),
  /** Mensen met een account, ongeacht de status. */
  accounts: z.number().int(),
  /** Accounts die al geactiveerd zijn. */
  activated: z.number().int(),
  /** Accounts die nog op hun eerste aanmelding wachten. */
  pending: z.number().int(),
});
export type ActivationPoint = z.infer<typeof activationPointSchema>;

export const activationReportSchema = z.object({
  from: z.string(),
  to: z.string(),
  series: z.array(activationPointSchema),
  /** De stand van vandaag, zodat het scherm geen laatste punt hoeft op te zoeken. */
  current: activationPointSchema.nullable(),
});
export type ActivationReport = z.infer<typeof activationReportSchema>;

/**
 * Iemand die de app lang niet geopend heeft.
 *
 * Mét naam, en dat is de uitzondering in deze module. Een lijst zonder namen zou de vraag "wie
 * moeten we aanspreken" niet beantwoorden, en dat is de enige reden dat deze lijst bestaat. Daarom
 * zit ze achter een eigen recht **én** een tenantvlag — dezelfde twee sloten als de namenlijst bij
 * bereik (ADR-0014).
 */
export const dormantPersonSchema = z.object({
  personId: z.guid(),
  displayName: z.string(),
  employeeNumber: z.string().nullable(),
  /** Leeg betekent: nooit aangemeld. Dat is iets anders dan lang niet. */
  lastSeenAt: z.string().datetime().nullable(),
  daysSinceLastSeen: z.number().int().nullable(),
  /** Of pushmeldingen aanstaan. Uit betekent: hij merkt niets van een nieuw bericht. */
  pushEnabled: z.boolean(),
  accountStatus: z.enum(['pending_activation', 'active', 'disabled', 'none']),
});
export type DormantPerson = z.infer<typeof dormantPersonSchema>;

export const dormantReportSchema = z.object({
  /** Vanaf hoeveel dagen stilte iemand in deze lijst komt. */
  thresholdDays: z.number().int(),
  items: z.array(dormantPersonSchema),
  /**
   * Het aantal, ook als de namen niet mogen. Zo ziet een beheerder zonder het recht wél dát er
   * twaalf mensen stil zijn — een aantal zonder namen mag altijd (ADR-0049).
   */
  total: z.number().int(),
  namesVisible: z.boolean(),
  namesWithheldReason: z.enum(['no_permission', 'disabled_for_tenant']).nullable(),
});
export type DormantReport = z.infer<typeof dormantReportSchema>;

// --- Het maandrapport ----------------------------------------------------------------

/** Hoe vaak een ontvanger het rapport krijgt (FBK-11). */
export const reportFrequencySchema = z.enum(['weekly', 'biweekly', 'monthly']);
export type ReportFrequency = z.infer<typeof reportFrequencySchema>;

export const reportSubscriptionSchema = z.object({
  id: z.guid(),
  email: z.string().email(),
  frequency: reportFrequencySchema.default('monthly'),
  /** Of deze ontvanger het rapport nog krijgt. Uitzetten in plaats van weghalen. */
  enabled: z.boolean(),
});
export type ReportSubscription = z.infer<typeof reportSubscriptionSchema>;

export const reportSubscriptionListSchema = z.object({
  items: z.array(reportSubscriptionSchema),
});
export type ReportSubscriptionList = z.infer<typeof reportSubscriptionListSchema>;

export const addReportSubscriptionRequestSchema = z.object({
  email: z.string().trim().email('Vul een geldig e-mailadres in.').max(320),
  frequency: reportFrequencySchema.default('monthly'),
});

export const updateReportSubscriptionRequestSchema = z.object({
  frequency: reportFrequencySchema,
});
export type UpdateReportSubscriptionRequest = z.infer<typeof updateReportSubscriptionRequestSchema>;
export type AddReportSubscriptionRequest = z.infer<typeof addReportSubscriptionRequestSchema>;

/** Wanneer de projectie voor het laatst herrekend is; het scherm toont dat eerlijk. */
export const reportFreshnessSchema = z.object({
  /** `YYYY-MM-DD` van de laatste dag die in de projectie staat, of leeg als er niets is. */
  lastComputedDate: z.string().nullable(),
  computedAt: z.string().datetime().nullable(),
});
export type ReportFreshness = z.infer<typeof reportFreshnessSchema>;

// --- Betrokkenheid (FBK-11) ----------------------------------------------------------

/**
 * Wanneer er gelezen wordt: per weekdag (0 = maandag) en uur, hoe vaak een bericht uit de periode
 * voor het eerst gezien werd, in de tijdzone van het bedrijf. Voor het beste publicatiemoment.
 */
export const bestMomentSchema = z.object({
  from: z.string(),
  to: z.string(),
  timeZone: z.string(),
  /** 7 rijen (maandag eerst) van 24 uur. */
  grid: z.array(z.array(z.number().int())),
  /** Het totaal; onder de k-drempel is `grid` leeg (ADR-0049). */
  total: z.number().int(),
  suppressed: z.boolean(),
});
export type BestMoment = z.infer<typeof bestMomentSchema>;

/** Wat er op één bericht gebeurde: de betrokkenheid en de klikken per link. Aantallen, geen namen. */
export const postEngagementSchema = z.object({
  postId: z.guid(),
  recipients: z.number().int(),
  seen: z.number().int(),
  acknowledged: z.number().int(),
  reactions: z.number().int(),
  comments: z.number().int(),
  pollVotes: z.number().int(),
  /** Interacties gedeeld door ontvangers, 0 tot 1; null zonder ontvangers. */
  engagementRate: z.number().nullable(),
  clicks: z.number().int(),
  uniqueClickers: z.number().int(),
  links: z.array(z.object({ url: z.string(), total: z.number().int(), unique: z.number().int() })),
  /** Gezien per uur sinds publicatie, de eerste 72 uur: de leescurve. */
  seenCurve: z.array(z.number().int()),
});
export type PostEngagement = z.infer<typeof postEngagementSchema>;
